// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useTheme } from '@mui/material';

// TYPES ---------------------------------------------------------------- //

export interface MapRouteComputed {
  durationSec: number;
  distanceMeters: number;
}

export interface UseMapRouteOptions {
  mapId?: string;
  pickup?: { lat: number; lng: number } | null;
  delivery?: { lat: number; lng: number } | null;
  enabled?: boolean;
  strokeColor?: string;
  fitBounds?: boolean;
  fitBoundsPadding?: number;
  onLoadingChange?: (loading: boolean) => void;
  onRouteComputed?: (route: MapRouteComputed) => void;
  onError?: (status: string | null) => void;
}

// HOOK ---------------------------------------------------------------- //

/**
 * Render a pickup → delivery driving route on a `<Map>`: theme-colored main
 * polyline plus a 12-layer white gradient traveling along the route.
 *
 * Owns the DirectionsService request, polyline + animation refs, and cleanup.
 * Drop inside any component that lives within a `<Map>`; the hook will fit
 * bounds (default 50px padding), animate unless `prefers-reduced-motion`, and
 * clear everything on unmount or when endpoints change.
 */
export const useMapRoute = ({
  mapId,
  pickup,
  delivery,
  enabled = true,
  strokeColor,
  fitBounds = true,
  fitBoundsPadding = 50,
  onLoadingChange,
  onRouteComputed,
  onError,
}: UseMapRouteOptions) => {
  const theme = useTheme();
  const map = useMap(mapId);
  const googleMapsService = useMapsLibrary('maps');

  const directionsServiceRef = React.useRef<google.maps.DirectionsService | null>(null);
  const routeMainRef = React.useRef<google.maps.Polyline | null>(null);
  const lightLayersRef = React.useRef<google.maps.Polyline[]>([]);
  const animationFrameRef = React.useRef<number>(0);

  const onLoadingChangeRef = React.useRef(onLoadingChange);
  const onRouteComputedRef = React.useRef(onRouteComputed);
  const onErrorRef = React.useRef(onError);
  React.useEffect(() => {
    onLoadingChangeRef.current = onLoadingChange;
    onRouteComputedRef.current = onRouteComputed;
    onErrorRef.current = onError;
  }, [onLoadingChange, onRouteComputed, onError]);

  React.useEffect(() => {
    if (!googleMapsService) return;
    directionsServiceRef.current = new window.google.maps.DirectionsService();
  }, [googleMapsService]);

  const clearRoute = React.useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = 0;
    }
    if (routeMainRef.current) {
      routeMainRef.current.setMap(null);
      routeMainRef.current = null;
    }
    for (const layer of lightLayersRef.current) {
      layer.setMap(null);
    }
    lightLayersRef.current = [];
  }, []);

  const pickupLat = pickup?.lat;
  const pickupLng = pickup?.lng;
  const deliveryLat = delivery?.lat;
  const deliveryLng = delivery?.lng;

  React.useEffect(() => {
    if (!map) return;

    if (
      !enabled ||
      pickupLat == null ||
      pickupLng == null ||
      deliveryLat == null ||
      deliveryLng == null
    ) {
      clearRoute();
      onErrorRef.current?.(null);
      return;
    }

    if (fitBounds) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend({ lat: pickupLat, lng: pickupLng });
      bounds.extend({ lat: deliveryLat, lng: deliveryLng });
      map.fitBounds(bounds, {
        top: fitBoundsPadding,
        bottom: fitBoundsPadding,
        left: fitBoundsPadding,
        right: fitBoundsPadding,
      });
    }

    const directionsService = directionsServiceRef.current;
    if (!directionsService) return;

    let aborted = false;
    clearRoute();

    const routeColor = strokeColor || theme?.palette?.primary?.main || '#2080ff';

    onLoadingChangeRef.current?.(true);
    onErrorRef.current?.(null);

    directionsService.route(
      {
        origin: `${pickupLat},${pickupLng}`,
        destination: `${deliveryLat},${deliveryLng}`,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (res, status) => {
        onLoadingChangeRef.current?.(false);
        if (aborted) return;
        if (status !== 'OK' || !res) {
          console.error('Directions request failed: ' + status);
          onErrorRef.current?.(status);
          return;
        }

        const leg = res.routes[0]?.legs?.[0];
        if (leg) {
          onRouteComputedRef.current?.({
            durationSec: leg.duration?.value ?? 0,
            distanceMeters: leg.distance?.value ?? 0,
          });
        }

        const routePath = res.routes[0].overview_path.map(p => ({
          lat: p.lat(),
          lng: p.lng(),
        }));

        if (routePath.length < 2) return;

        // Main route line
        routeMainRef.current = new google.maps.Polyline({
          path: routePath,
          geodesic: true,
          strokeColor: routeColor,
          strokeWeight: 5,
          strokeOpacity: 1,
          zIndex: 3,
          clickable: false,
          map,
        });

        // Animated light gradient traveling along the route.
        // Stacks multiple white polyline segments of decreasing length, all
        // centered on the same moving point. Where more layers overlap the
        // cumulative opacity is higher (brighter); at the edges only the
        // outermost layer is visible, creating a smooth gradient fade.
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const totalPoints = routePath.length;
        const LAYER_COUNT = 12;
        const LIGHT_WINDOW = Math.max(Math.floor(totalPoints * 0.24), 8);
        const LAYER_OPACITY = 0.08; // LAYER_COUNT * LAYER_OPACITY = max opacity of the middle segment.

        const gradientLayers: google.maps.Polyline[] = [];
        for (let i = 0; i < LAYER_COUNT; i++) {
          gradientLayers.push(
            new google.maps.Polyline({
              path: [],
              geodesic: true,
              strokeColor: '#ffffff',
              strokeWeight: 5,
              strokeOpacity: LAYER_OPACITY,
              zIndex: 5,
              clickable: false,
              visible: false,
              map,
            })
          );
        }
        lightLayersRef.current = gradientLayers;

        if (!prefersReducedMotion) {
          const totalTravel = totalPoints + LIGHT_WINDOW;
          const TARGET_CYCLE_SECONDS = 4;
          const SPEED = totalTravel / (TARGET_CYCLE_SECONDS * 60);
          let position = 0;

          const animate = () => {
            position = (position + SPEED) % totalTravel;
            const centerIdx = Math.floor(position);

            for (let i = 0; i < LAYER_COUNT; i++) {
              const halfSpan = Math.max(1, Math.floor(((LIGHT_WINDOW / 2) * (LAYER_COUNT - i)) / LAYER_COUNT));
              const segStart = Math.max(0, centerIdx - halfSpan);
              const segEnd = Math.min(totalPoints - 1, centerIdx + halfSpan);
              const segPath = routePath.slice(segStart, segEnd + 1);

              if (segPath.length >= 2) {
                gradientLayers[i].setPath(segPath);
                gradientLayers[i].setVisible(true);
              } else {
                gradientLayers[i].setVisible(false);
              }
            }

            animationFrameRef.current = requestAnimationFrame(animate);
          };

          animationFrameRef.current = requestAnimationFrame(animate);
        }
      }
    );

    return () => {
      aborted = true;
      clearRoute();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, enabled, pickupLat, pickupLng, deliveryLat, deliveryLng, strokeColor, fitBounds, fitBoundsPadding]);
};
